import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs';

import { CatsActionModeEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { IExtDocument } from '@shared-interfaces/documents';

import { ICatRecord } from '../interfaces/incoming';
import { IAnyCatsType, ITableConfigEntity } from '../interfaces/internal-interfaces';
import { BillingTimesheet } from '../scripts/BillingTimesheet';
import { ContractHandler } from '../scripts/contracts/contract-handler';
import { ExtDocument } from '../scripts/extDocument';
import { ProductionGroup } from '../scripts/Production-Group/ProductionGroup';
import { ISapRequestFilterQueryProps } from './api.model';
import { DatahubService } from './datahub.service';

export interface ITimesheetHeader {
  config: ITableConfigEntity;
  selectedValue: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class TimesheetHelperService {
  private readonly isWorkingSubject$ = new BehaviorSubject(false);
  private readonly timesheetCreatedSubject$ = new Subject<IExtDocument>();
  private readonly recordsMovedSubject$ = new Subject<ICatRecord[]>();

  public readonly isWorking$ = this.isWorkingSubject$.asObservable();
  public readonly timesheetCreated$ = this.timesheetCreatedSubject$.asObservable();
  public readonly recordsMoved$ = this.recordsMovedSubject$.asObservable();
  public readonly timesheetDescriptionMinLength = 6;

  constructor(private readonly datahubSvc: DatahubService) {}

  public getActivityDescription(record: IAnyCatsType): string {
    const activityConfig = record.__config?.find((c) => c.header === 'Activity') as ITableConfigEntity<keyof IAnyCatsType>;
    if (activityConfig != null) {
      const val$ = record[activityConfig.key];
      if (typeof val$ === 'string') return val$;
    }
    return '';
  }

  public moveSingleRecord$(
    containingTimesheet: BillingTimesheet,
    targetDocument: ExtDocument,
    recordToMove: IAnyCatsType,
  ): Observable<ICatRecord> {
    this.isWorkingSubject$.next(true);

    return this.updateSingleRecord$(containingTimesheet, targetDocument, recordToMove).pipe(
      tap({
        next: (result) => {
          this.recordsMovedSubject$.next([result]);
          this.isWorkingSubject$.next(false);
        },
        error: () => {
          this.isWorkingSubject$.next(false);
        },
      }),
    );
  }

  public moveAllProductionGroupRecords$(
    containingTimesheet: BillingTimesheet,
    targetDocument: ExtDocument,
    productionGroup: ProductionGroup,
  ): Observable<{ records: ICatRecord[]; productionGroup: ProductionGroup }> {
    this.isWorkingSubject$.next(true);

    //return a single joined observable that will complete when everything in the array is complete
    return this.updateProdGroupRecords$(containingTimesheet, targetDocument, productionGroup).pipe(
      tap({
        next: (allMovedRecords) => {
          this.recordsMovedSubject$.next(allMovedRecords);
          this.isWorkingSubject$.next(false);
        },
        error: () => {
          this.isWorkingSubject$.next(false);
        },
      }),
      switchMap((records) => of({ records, productionGroup })),
    );
  }

  public create$(
    contract: ContractHandler,
    description: string,
    newHeaders: ITimesheetHeader[] = [],
    status: TimesheetStatusEnum = TimesheetStatusEnum.Created,
  ): Observable<IExtDocument> {
    this.isWorkingSubject$.next(true);

    const owner = this.datahubSvc.user.getApprover();

    const body: ISapRequestFilterQueryProps = {
      Owner: owner,
      Salesdoc: contract.value.ServiceOrder,
      Dateentered: this.datahubSvc.weDate.getUnixString(),
      Pernr: this.datahubSvc.user.getEmpId(),
      CustGroup: contract.value.CustGroup,
      CondGroup: contract.value.CondGroup,
      Costcenter1: contract.value.ProfitCenter,
      CustomerDescription: contract.value.CustomerName,
      ContractDescription: contract.value.ItemText,
      Description: description,
      Status: status,
    };

    //if headers were provided add them to the outgoing request body
    if (newHeaders.length > 0) {
      for (const header of newHeaders) {
        body[header.config.key] = header.selectedValue;
      }
    }

    return this.datahubSvc.apiService.makeTimesheet$(body).pipe(
      tap({
        next: (result: IExtDocument) => {
          this.timesheetCreatedSubject$.next(result);
          this.isWorkingSubject$.next(false);
        },
        error: () => {
          this.isWorkingSubject$.next(false);
        },
      }),
    );
  }

  private updateSingleRecord$(
    containingTimesheet: BillingTimesheet,
    targetDocument: ExtDocument,
    recordToMove: IAnyCatsType,
  ): Observable<ICatRecord> {
    //Change the statuses
    containingTimesheet.updateExtDocumentStatus([targetDocument.Documentno, recordToMove.Extdocumentno]);
    recordToMove.updateProperty('Extdocumentno', targetDocument.Documentno);

    const recordAsCats = recordToMove.getCatsFormattedRecord();
    return this.datahubSvc.catsDB.simpleSingleCats$(CatsActionModeEnum.Update, {
      ...recordAsCats,
      Extdocumentno_X: true, //this tells SAP we are editing the specific property
    });
  }

  private updateProdGroupRecords$(
    containingTimesheet: BillingTimesheet,
    targetDocument: ExtDocument,
    prodGroupToMove: ProductionGroup,
  ): Observable<ICatRecord[]> {
    prodGroupToMove.ExtDocumentno = targetDocument.Documentno;
    const recordsToUpdate = prodGroupToMove.getRecordsForMoveTimesheet().map((record) => {
      //Change the statuses
      containingTimesheet.updateExtDocumentStatus([targetDocument.Documentno, record.Extdocumentno ?? '']);
      record.Extdocumentno = targetDocument.Documentno;

      return {
        ...record,
        Extdocumentno_X: true, //this tells SAP we are editing the specific property
      };
    });

    return this.datahubSvc.catsDB.simpleMultiCats$(recordsToUpdate, CatsActionModeEnum.Update);
  }
}
